import React from 'react';
import { MapPin, Mail, Phone, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import { contactInfo } from '../data/mockData';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (hash) => {
    const element = document.querySelector(hash);
    if (element) {
      const offset = 80;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <footer className="relative bg-black border-t border-white/10 pt-20 pb-10">
      <div className="container mx-auto px-[7.6923%]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand Column */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4">
              LFX<span className="text-blue-500">Pro</span>
            </h3>
            <p className="text-base text-white/70 mb-4 leading-relaxed">
              Engineered for Experience
            </p>
            <p className="text-sm text-white/60 leading-relaxed">
              Premium professional lighting solutions engineered in Germany for uncompromising performance.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="#products"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('#products');
                  }}
                  className="text-base text-white/70 hover:text-blue-500 transition-colors duration-300"
                >
                  Products
                </a>
              </li>
              <li>
                <a
                  href="#gallery"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('#gallery');
                  }}
                  className="text-base text-white/70 hover:text-blue-500 transition-colors duration-300"
                >
                  Gallery
                </a>
              </li>
              <li>
                <a
                  href="#about"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('#about');
                  }}
                  className="text-base text-white/70 hover:text-blue-500 transition-colors duration-300"
                >
                  About Us
                </a>
              </li>
              <li>
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('#contact');
                  }}
                  className="text-base text-white/70 hover:text-blue-500 transition-colors duration-300"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Contact</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="text-sm text-white/70 hover:text-blue-500 transition-colors duration-300 flex items-start gap-2"
                >
                  <Mail size={18} className="flex-shrink-0 mt-0.5" />
                  <span>{contactInfo.email}</span>
                </a>
              </li>
              <li>
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="text-sm text-white/70 hover:text-blue-500 transition-colors duration-300 flex items-start gap-2"
                >
                  <Phone size={18} className="flex-shrink-0 mt-0.5" />
                  <span>{contactInfo.phone}</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Locations</h4>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-blue-400 mb-1">India</p>
                <p className="text-sm text-white/70 leading-relaxed">
                  {contactInfo.india.address}, {contactInfo.india.city}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-blue-400 mb-1">Germany</p>
                <p className="text-sm text-white/70 leading-relaxed">
                  {contactInfo.germany.address}, {contactInfo.germany.city}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media & Copyright */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Social Media */}
            <div className="flex gap-4">
              <a
                href={contactInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center hover:bg-blue-600 hover:border-blue-600 transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook size={18} className="text-white" />
              </a>
              <a
                href={contactInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center hover:bg-blue-600 hover:border-blue-600 transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram size={18} className="text-white" />
              </a>
              <a
                href={contactInfo.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center hover:bg-blue-600 hover:border-blue-600 transition-all duration-300"
                aria-label="LinkedIn"
              >
                <Linkedin size={18} className="text-white" />
              </a>
              <a
                href={contactInfo.social.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center hover:bg-blue-600 hover:border-blue-600 transition-all duration-300"
                aria-label="YouTube"
              >
                <Youtube size={18} className="text-white" />
              </a>
            </div>

            {/* Copyright */}
            <p className="text-sm text-white/50">
              © {currentYear} LFXPro Lights. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
